import clsx from 'clsx';
import activeElement from 'dom-helpers/activeElement';
import contains from 'dom-helpers/query/contains';
import keycode from 'keycode';
import React, { cloneElement, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import elementType from 'prop-types-extra/lib/elementType';
import isRequiredForA11y from 'prop-types-extra/lib/isRequiredForA11y';
import { uncontrollable } from 'uncontrollable';
import warning from 'warning';
import ButtonGroup from '../ButtonGroup/ButtonGroup.js';
import DropdownMenu from '../bootstrap/DropdownMenu.js';
import DropdownToggle from '../bootstrap/DropdownToggle';
import { prefix } from '../bootstrap/utils/bootstrapUtils';
import createChainedFunction from '../bootstrap/utils/createChainedFunction';
import ValidComponentChildren from '../bootstrap/utils/ValidComponentChildren';
import { jsx as _jsx } from "react/jsx-runtime";
function Dropdown(_ref) {
  let {
    componentClass: Component = ButtonGroup,
    id,
    dropup,
    disabled,
    pullRight,
    open,
    onSelect,
    role,
    bsClass = 'dropdown',
    className,
    rootCloseEvent,
    children,
    onToggle,
    ...props
  } = _ref;
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const focusInDropdownRef = useRef(false);
  const lastOpenEventTypeRef = useRef(null);
  const focus = useCallback(() => {
    const toggle = toggleRef.current;
    if (toggle && toggle.focus) {
      toggle.focus();
    }
  }, []);
  const focusNextOnOpen = useCallback(() => {
    const menu = menuRef.current;
    if (!menu || !menu.focusNext) {
      return;
    }
    if (lastOpenEventTypeRef.current === 'keydown' || role === 'menuitem') {
      menu.focusNext();
    }
  }, [role]);
  const toggleOpen = useCallback((event, eventDetails) => {
    const newOpen = !open;
    if (newOpen) {
      lastOpenEventTypeRef.current = eventDetails.source;
    }
    if (onToggle) {
      onToggle(newOpen, event, eventDetails);
    }
  }, [open, onToggle]);
  const handleClick = useCallback(event => {
    if (disabled) {
      return;
    }
    toggleOpen(event, {
      source: 'click'
    });
  }, [disabled, toggleOpen]);
  const handleClose = useCallback((event, eventDetails) => {
    if (!open) {
      return;
    }
    toggleOpen(event, eventDetails);
  }, [open, toggleOpen]);
  const handleKeyDown = useCallback(event => {
    if (disabled) {
      return;
    }
    switch (event.keyCode) {
      case keycode.codes.down:
        if (!open) {
          toggleOpen(event, {
            source: 'keydown'
          });
        } else if (menuRef.current && menuRef.current.focusNext) {
          menuRef.current.focusNext();
        }
        event.preventDefault();
        break;
      case keycode.codes.esc:
      case keycode.codes.tab:
        handleClose(event, {
          source: 'keydown'
        });
        break;
      default:
    }
  }, [disabled, open, toggleOpen, handleClose]);

  // Track focus state before closing (replaces UNSAFE_componentWillUpdate)
  useEffect(() => {
    if (!open) {
      const menu = menuRef.current;
      if (menu) {
        focusInDropdownRef.current = contains(menu, activeElement(document));
      }
    }
  }, [open]);

  // Handle focus after open state changes (replaces componentDidUpdate)
  useEffect(() => {
    if (open) {
      focusNextOnOpen();
    } else if (focusInDropdownRef.current) {
      // if focus hasn't already moved from the menu let's return it to the toggle
      focusInDropdownRef.current = false;
      focus();
    }
  }, [open, focusNextOnOpen, focus]);
  const renderMenu = useCallback((child, _ref2) => {
    let {
      id: menuId,
      onSelect: menuOnSelect,
      rootCloseEvent: menuRootCloseEvent,
      ...menuProps
    } = _ref2;
    let ref = c => {
      menuRef.current = c;
    };
    if (typeof child.ref === 'string') {
      process.env.NODE_ENV !== "production" ? warning(false, 'String refs are not supported on `<Dropdown.Menu>` components. ' + 'To apply a ref to the component use the callback signature:\n\n ' + 'https://facebook.github.io/react/docs/more-about-refs.html#the-ref-callback-attribute') : void 0;
    } else {
      ref = createChainedFunction(child.ref, ref);
    }
    return /*#__PURE__*/cloneElement(child, {
      ...menuProps,
      ref,
      labelledBy: menuId,
      bsClass: prefix(menuProps, 'menu'),
      onClose: createChainedFunction(child.props.onClose, handleClose),
      onSelect: createChainedFunction(child.props.onSelect, menuOnSelect, (key, event) => handleClose(event, {
        source: 'select'
      })),
      rootCloseEvent: menuRootCloseEvent
    });
  }, [handleClose]);
  const renderToggle = useCallback((child, toggleProps) => {
    let ref = c => {
      toggleRef.current = c;
    };
    if (typeof child.ref === 'string') {
      process.env.NODE_ENV !== "production" ? warning(false, 'String refs are not supported on `<Dropdown.Toggle>` components. ' + 'To apply a ref to the component use the callback signature:\n\n ' + 'https://facebook.github.io/react/docs/more-about-refs.html#the-ref-callback-attribute') : void 0;
    } else {
      ref = createChainedFunction(child.ref, ref);
    }
    return /*#__PURE__*/cloneElement(child, {
      ...toggleProps,
      ref,
      bsClass: prefix(toggleProps, 'toggle'),
      onClick: createChainedFunction(child.props.onClick, handleClick),
      onKeyDown: createChainedFunction(child.props.onKeyDown, handleKeyDown)
    });
  }, [handleClick, handleKeyDown]);
  const classes = {
    [bsClass]: true,
    open,
    disabled
  };
  if (dropup) {
    classes[bsClass] = false;
    classes.dropup = true;
  }

  // This intentionally forwards bsSize and bsStyle (if set) to the
  // underlying component, to allow it to render size and style variants.

  return /*#__PURE__*/_jsx(Component, {
    ...props,
    className: clsx(className, classes),
    children: ValidComponentChildren.map(children, child => {
      // Check component type directly
      if (child.type === DropdownToggle) {
        return renderToggle(child, {
          id,
          disabled,
          open,
          role,
          bsClass
        });
      }
      if (child.type === DropdownMenu) {
        return renderMenu(child, {
          id,
          open,
          pullRight,
          bsClass,
          onSelect,
          rootCloseEvent
        });
      }
      return child;
    })
  });
}
const UncontrolledDropdown = uncontrollable(Dropdown, {
  open: 'onToggle'
});
UncontrolledDropdown.Toggle = DropdownToggle;
UncontrolledDropdown.Menu = DropdownMenu;
export default UncontrolledDropdown;